import styled from 'styled-components';
import { Typography } from '@mui/material';
import { formatNumbersToShowDiff, numberWithCommas } from '../../utils/common';
import { localizeDate } from '../../utils/datetime';
import ChartTooltip from '../ChartTooltip';
import { StatusChip } from '../Chip';
import { ChipStatus } from '../Chip/StatusChip';
import Button from '../Button';
import { FlexSpace } from '../Flex';
import { space } from '../../style/utils';
import { isFailurePoint } from './utils';
import { RESULT_FEEDBACK_BUTTON_LABEL } from './constants';
import ChangepointTooltipSection from './ChangepointTooltipSection';

type ValueFormatter = (value: number) => string;

const TooltipItem = styled(FlexSpace)`
  &:not(:last-of-type) {
    margin-bottom: ${space('sm')};
  }
`;

const defaultValueFormatter = (value: number) => numberWithCommas(value, 2);

const AnomalyChartTooltip = ({
  active,
  data,
  metricsTitle,
  valueColor,
  rangeColor,
  status = 'fail',
  valueFormatter = defaultValueFormatter,
  shouldExpectDataRange = true,
  onFeedbackClick,
  changepoint,
}: {
  active: boolean;
  data?: {
    value: number;
    min_value: number | null;
    max_value: number | null;
    start_time: string;
    end_time: string;
    metric_name: string;
    fail_on_zero: boolean;
  };
  metricsTitle?: string;
  valueColor?: string;
  rangeColor?: string;
  status?: ChipStatus;
  valueFormatter?: ValueFormatter;
  shouldExpectDataRange?: boolean;
  onFeedbackClick?: () => void;
  changepoint?: { label: string; onRemoveClick?: () => void };
}) => {
  if (data?.value == null) return null;

  if (
    active &&
    data &&
    data.min_value !== undefined &&
    data.max_value !== undefined
  ) {
    const minExists = data.min_value != null;
    const maxExists = data.max_value != null;
    const rangeExists = minExists || maxExists;
    const isFailure = isFailurePoint(data);

    const startDate =
      data.start_time &&
      localizeDate(data.start_time).isValid() &&
      localizeDate(data.start_time).format('L LT');
    const endDate = localizeDate(data.end_time).format('L LT');

    const expectedRangeText = ((): string => {
      if (data.min_value != null && data.max_value != null) {
        const [minVal, maxVal] = formatNumbersToShowDiff(
          Math.max(0, data.min_value),
          data.max_value,
        );
        return `${valueFormatter(minVal)} - ${valueFormatter(maxVal)}`;
      }
      if (data.min_value != null) {
        return `≥ ${valueFormatter(data.min_value)}`;
      }
      if (data.max_value != null) {
        return `≤ ${valueFormatter(data.max_value)}`;
      }
      return '';
    })();

    const defaultValueColor = rangeExists
      ? isFailure
        ? 'status.error.bright'
        : 'status.success.bright'
      : 'content.primary';

    return (
      <ChartTooltip>
        {isFailure && <StatusChip sx={{ mb: 2 }} status={status} />}

        <Typography sx={{ mb: 1 }}>
          {startDate && startDate !== endDate
            ? `${startDate} - ${endDate}`
            : endDate}
        </Typography>

        <TooltipItem>
          <Typography sx={{ mr: 1 }} color={valueColor || defaultValueColor}>
            {metricsTitle || data?.metric_name}:
          </Typography>
          <Typography
            color={valueColor || defaultValueColor}
            sx={{ marginLeft: 'auto' }}
          >
            {valueFormatter(data.value)}
          </Typography>
        </TooltipItem>

        {!rangeExists ? (
          <TooltipItem>
            <Typography color={rangeColor}>
              {status === 'no_data' || !shouldExpectDataRange
                ? 'No expected range data'
                : 'Data point excluded from the test'}
            </Typography>
          </TooltipItem>
        ) : (
          <TooltipItem>
            <Typography color={rangeColor} sx={{ mr: 1 }}>
              Expected:
            </Typography>
            <Typography color={rangeColor}>{expectedRangeText}</Typography>
            {data?.fail_on_zero && (
              <Typography color={rangeColor}>and not zero</Typography>
            )}
          </TooltipItem>
        )}

        {changepoint && (
          <ChangepointTooltipSection
            label={changepoint.label}
            onRemoveClick={changepoint.onRemoveClick}
          />
        )}

        {isFailure && onFeedbackClick && (
          <Button
            size="extraSmall"
            variant="outlined"
            color="secondary"
            sx={{ mt: 1 }}
            onClick={onFeedbackClick}
          >
            {RESULT_FEEDBACK_BUTTON_LABEL}
          </Button>
        )}
      </ChartTooltip>
    );
  }

  return null;
};

export default AnomalyChartTooltip;
